import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import {
	buildResolvedSmartLinkState,
	partitionResolvedLinks,
} from '@confluence/content-smartlinks/entry-points/buildResolvedSmartLinkState.ts';
import { fetchORSForSSR } from '@confluence/content-smartlinks/entry-points/fetchORSForSSR.ts';
import { expVal } from '@confluence/feature-experiments';
import { getInitialState } from '@confluence/content-smartlinks/entry-points/getInitialState.ts';
import { timeGuard } from '@confluence/query-preloader-tools/entry-points/timeGuard.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getQueryBoundary } from '@confluence/query-preloader-tools/entry-points/queryBoundaryStorage.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { shortcutsSmartLinksQuery } from './shortcutsSmartLinksQuery.ts';
import type {
	shortcutsSmartLinksQuery$data,
	shortcutsSmartLinksQuery$variables,
} from './__generated__/shortcutsSmartLinksQuery.graphql.ts';

declare global {
	interface Window {
		__SSR_SHORTCUTS__?: { [key: string]: any } | {};
	}
}

const SMART_LINK_MAX_TIME = 250;

const getSidenavSmartLinksORSMeasureName = () => {
	const queryBoundary = getQueryBoundary();

	if (!queryBoundary || queryBoundary === 'default') {
		return 'ssr-app/prepare/preloadQuery/fetch:sidenavSmartLinksORS';
	}

	return `ssr-app/prepare/preloadQuery/dataFetcher:${queryBoundary}/fetch:sidenavSmartLinksORS`;
};

/**
 * Resolves a list of smart links via ORS and returns their SmartCardProvider initial state.
 */
const fetchORSInitialState = async (
	links: any[],
	objectResolverURL: string,
	userContext: any,
): Promise<Record<string, any>> => {
	let orsInitialState: Record<string, any> = {};

	await SSRMeasures.run(getSidenavSmartLinksORSMeasureName(), async () => {
		const timeout = StatsigConfigurations.getNumber(
			'confluence_ssr_sidenav_smart_link_max_time',
			SMART_LINK_MAX_TIME,
		);

		const resolvedSmartLinks = await timeGuard(
			async () => {
				try {
					const res = await fetchORSForSSR({
						url: `${objectResolverURL}?timeout=${timeout}`,
						userContext,
						body: JSON.stringify(links),
					});
					if (!res.ok) {
						throw new Error(`${res.status} ${res.statusText}`);
					}
					return res.json();
				} catch (e) {
					ssrPrepareErrorsClient.submitError(
						new Error(`Smart link shortcuts ORS call failed: ${e.message}`),
					);
				}
				return [];
			},
			[],
			timeout,
		);

		if (resolvedSmartLinks.length) {
			orsInitialState = getInitialState(resolvedSmartLinks);
		}
	});

	return orsInitialState;
};

type ShortcutsRouteData = {
	spaceKey: string;
	cloudId: string;
	data: shortcutsSmartLinksQuery$data | null;
};

export const preloadShortcutsSmartLinks = async (
	spaceKey: string,
	routeData?: ShortcutsRouteData,
): Promise<void> => {
	const { customURLs, userContext, cloudId } = getSSRRenderInputs();
	const objectResolverURL = customURLs?.['object-resolver-service'];

	// Use the confluence_shortcutSmartLinksBySpaceKey query, which returns enriched metadata for
	// Confluence links, skipping ORS for those.
	// Non-Confluence links (Jira, external) still fall back to ORS.
	if (!cloudId) {
		return;
	}

	// Only accept enrichment for this exact route. An empty nodes array is valid;
	// absent/null deferred data must retain the standalone-query fallback.
	const res =
		routeData?.spaceKey === spaceKey &&
		routeData.cloudId === cloudId &&
		routeData.data?.confluence_shortcutSmartLinksBySpaceKey?.nodes != null &&
		!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_shortcuts_enrichment') &&
		expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
			? { data: routeData.data }
			: await query<shortcutsSmartLinksQuery$data, shortcutsSmartLinksQuery$variables>({
					query: shortcutsSmartLinksQuery,
					variables: { spaceKey, cloudId },
				});

	// Map GQL nodes to SmartLinkType — the common shape used by partitionResolvedLinks
	// and buildResolvedSmartLinkState, keeping logic uniform with preloadContentSmartLinks.
	// Note: resolved and canView are always the same value from the BE.
	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
	const allLinks = (res?.data?.confluence_shortcutSmartLinksBySpaceKey?.nodes ?? [])
		.filter(
			(node): node is NonNullable<typeof node> & { url: string } =>
				node !== null && node !== undefined && Boolean(node.url),
		)
		.map((node) => ({
			appearance: 'inline' as const,
			resourceUrl: node.url,
			title: node.customTitle || node.title || null,
			emojiTitle: node.emojiTitle ?? null,
			canView: node.canView ?? null,
			product: node.product ?? null,
			resourceType: node.resourceType ?? null,
			subType: node.subType ?? null,
			iconUrl: node.iconUrl ?? null,
			resolved: node.resolved ?? null,
		}));

	if (!allLinks.length) {
		return;
	}

	// Partition: Confluence-resolved links skip ORS; unresolved (Jira, external) still need ORS.
	// Mirrors the same partitioning used in preloadContentSmartLinksWithSuspense.
	const { resolvedFromGql, linksForORS } = partitionResolvedLinks(allLinks);

	const confluenceInitialState =
		resolvedFromGql.length > 0 ? buildResolvedSmartLinkState(resolvedFromGql) : {};

	let orsInitialState: Record<string, any> = {};
	if (linksForORS.length > 0) {
		if (objectResolverURL) {
			// ORS only accepts { resourceUrl, appearance } — strip extra SmartLinkType fields
			// to avoid 400 errors.
			const sanitizedLinksForORS = linksForORS.map(({ resourceUrl, appearance }) => ({
				resourceUrl,
				appearance,
			}));
			orsInitialState = await fetchORSInitialState(
				sanitizedLinksForORS,
				objectResolverURL,
				userContext,
			);
		} else {
			ssrPrepareErrorsClient.submitError(
				new Error(
					`Smart link shortcuts: ${linksForORS.length} unresolved link(s) could not be sent to ORS — object-resolver-service URL is missing from SSR render inputs.`,
				),
			);
		}
	}

	// Format response to fit SmartCard Provider storeOptions initial state.
	// Save correctly formatted response to window obj so it can be used on SSR and SPA (no duplicate calls).
	const initialState = { ...confluenceInitialState, ...orsInitialState };
	if (Object.keys(initialState).length > 0) {
		window['__SSR_SHORTCUTS__'] = initialState;
	}

	window['__SSR_SHORTCUTS_STATS__'] = {
		gql: {
			totalLinks: allLinks.length,
			resolvedFromGqlCount: resolvedFromGql.length,
			linksForORSCount: linksForORS.length,
			skippedORS: linksForORS.length === 0,
		},
	};
};
